import { create } from "zustand";
import { repo } from "./repo";
import { CURRENT_STRATEGY, STRATEGY } from "./config";

// --- THE SWITCH CONFIGURATION ---
// Add any collection name here to completely turn off its real-time subscription
// and switch it to a smart, deduplicated one-time fetch model instead.
const STATIC_COLLECTIONS = [
  "categories",
  "subCategories",
  "brands",
  "vendors",
  "locations",
  "departments",
  "assets",
];

// 24-hour grace period to hold active subscriptions open during active user sessions
const GRACE_PERIOD_MS = 24 * 60 * 60 * 1000;

// Helper function to serialize collections and filters into unique cache keys
const getSubscriptionKey = (collectionName, filters) => {
  if (!filters || !Array.isArray(filters) || filters.length === 0) {
    return collectionName;
  }
  const sortedFilters = [...filters].sort((a, b) =>
    a.field.localeCompare(b.field),
  );
  return `${collectionName}-${JSON.stringify(sortedFilters)}`;
};

export const useDataStore = create((set, get) => ({
  loading: false,
  isRealTimeStrategy: CURRENT_STRATEGY === STRATEGY.NATIVE,
  subscriptions: {}, // Active listener unsubscribe functions
  queryCache: {}, // Local memory RAM storage for UI datasets
  subRefCount: {}, // Active screen reference counters
  subTeardownTimers: {}, // Active background countdown handles

  // Declarative selector to read cached snapshots synchronously inside components
  getData: (collectionName, filters = []) => {
    const key = getSubscriptionKey(collectionName, filters);
    return get().queryCache[key] || [];
  },

  // 1. Dual-Action Router (The Core Switch)
  subscribeToCollection: async (collectionName, filters = []) => {
    const subKey = getSubscriptionKey(collectionName, filters);

    // --- EVALUATE ROUTING SWITCH ---
    const shouldFetchOnce =
      STATIC_COLLECTIONS.includes(collectionName) ||
      CURRENT_STRATEGY === STRATEGY.FETCH;

    if (shouldFetchOnce) {
      // DEDUPLICATION: If data is already in memory cache, serve instantly for 0 reads
      if (get().queryCache[subKey]) {
        console.log(
          `[SWITCH -> FETCH] Cache hit for ${subKey}. 0 database reads used.`,
        );
        return;
      }

      set({ loading: true });
      try {
        console.log(
          `[SWITCH -> FETCH] Fetching static snapshot for: ${subKey}`,
        );
        const data = await repo.fetchData(collectionName, filters);
        set((state) => ({
          loading: false,
          queryCache: { ...state.queryCache, [subKey]: data },
        }));
      } catch (err) {
        console.error(`One-time fetch failed for ${subKey}:`, err);
        set({ loading: false });
      }
      return; // Exit out. No background subscription is ever established.
    }

    // --- REAL-TIME STREAMING ROUTE ---
    // Increment active screen reference count
    set((state) => ({
      subRefCount: {
        ...state.subRefCount,
        [subKey]: (state.subRefCount[subKey] || 0) + 1,
      },
    }));

    // If a background teardown was ticking for this stream, rescue it immediately
    if (get().subTeardownTimers[subKey]) {
      clearTimeout(get().subTeardownTimers[subKey]);
      set((state) => {
        const updatedTimers = { ...state.subTeardownTimers };
        delete updatedTimers[subKey];
        return { subTeardownTimers: updatedTimers };
      });
      console.log(
        `[STREAM RECOVERY] Rescued active stream for: ${subKey}. Re-read avoided.`,
      );
      return;
    }

    // Block secondary duplicate connections if already open
    if (get().subscriptions[subKey]) return;

    set((state) => ({
      loading: !state.queryCache[subKey], // Only spinner if cache is dead-cold
      subscriptions: {
        ...state.subscriptions,
        [subKey]: "PENDING_INITIALIZATION",
      },
    }));

    try {
      console.log(`[STREAM OPEN] Spin up live listener socket for: ${subKey}`);
      const unsubscribe = await repo.subscribeToUpdates(
        collectionName,
        filters,
        (updatedData) => {
          set((state) => ({
            loading: false,
            queryCache: { ...state.queryCache, [subKey]: updatedData },
          }));
        },
      );

      // Thread safety: Check if user unmounted while async handshakes took place
      if (get().subscriptions[subKey] === "PENDING_INITIALIZATION") {
        set((state) => ({
          subscriptions: { ...state.subscriptions, [subKey]: unsubscribe },
        }));
      } else {
        if (typeof unsubscribe === "function") unsubscribe();
      }
    } catch (err) {
      console.error(`Subscription assignment failed for ${subKey}:`, err);
      set((state) => {
        const updatedSubs = { ...state.subscriptions };
        delete updatedSubs[subKey];
        return { subscriptions: updatedSubs, loading: false };
      });
    }
  },

  // 2. Targeted Teardown with Grace Periods
  unsubscribeFromCollection: (
    collectionName,
    filters = [],
    customTimeout = GRACE_PERIOD_MS,
  ) => {
    const subKey = getSubscriptionKey(collectionName, filters);

    // Quick escape: Fetch-based collections do not hold persistent web socket lines
    if (
      STATIC_COLLECTIONS.includes(collectionName) ||
      CURRENT_STRATEGY === STRATEGY.FETCH
    ) {
      return;
    }

    const currentRefs = (get().subRefCount[subKey] || 1) - 1;
    set((state) => ({
      subRefCount: { ...state.subRefCount, [subKey]: currentRefs },
    }));

    // Only set standard countdown if all screens looking at this specific data are closed
    if (currentRefs <= 0) {
      console.log(
        `[GRACE PERIOD START] No active layouts looking at ${subKey}. Closing in 24h if untouched.`,
      );

      const timerId = setTimeout(() => {
        const unsubscribe = get().subscriptions[subKey];
        if (typeof unsubscribe === "function") {
          unsubscribe();
          console.log(
            `[HARD TEARDOWN] Closed active network channel for: ${subKey}`,
          );
        }
        set((state) => {
          const updatedSubs = { ...state.subscriptions };
          const updatedTimers = { ...state.subTeardownTimers };
          const updatedRefs = { ...state.subRefCount };
          delete updatedSubs[subKey];
          delete updatedTimers[subKey];
          delete updatedRefs[subKey];
          return {
            subscriptions: updatedSubs,
            subTeardownTimers: updatedTimers,
            subRefCount: updatedRefs,
          };
        });
      }, customTimeout);

      set((state) => ({
        subTeardownTimers: { ...state.subTeardownTimers, [subKey]: timerId },
      }));
    }
  },

  // 3. Manual Invalidation Action (Call this inside EditAssetModal after writing updates)
  invalidateCacheKey: (collectionName, filters = []) => {
    const subKey = getSubscriptionKey(collectionName, filters);
    set((state) => {
      const updatedCache = { ...state.queryCache };
      delete updatedCache[subKey];
      console.log(
        `[CACHE CLEARED] Invalidated key: ${subKey}. Next pull will read directly from database.`,
      );
      return { queryCache: updatedCache };
    });
  },

  // 4. Tab Visibility Loss Safety Valve
  handleTabVisibilityLoss: () => {
    const { subscriptions } = get();
    console.log(
      "[SAFETY VALVE] Tab hidden. Dropping streaming channels to protect read costs...",
    );

    Object.keys(subscriptions).forEach((subKey) => {
      const unsubscribe = subscriptions[subKey];
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
      set((state) => {
        const updatedSubs = { ...state.subscriptions };
        const updatedRefs = { ...state.subRefCount };
        const updatedTimers = { ...state.subTeardownTimers };

        delete updatedSubs[subKey];
        delete updatedRefs[subKey];
        if (updatedTimers[subKey]) {
          clearTimeout(updatedTimers[subKey]);
          delete updatedTimers[subKey];
        }
        return {
          subscriptions: updatedSubs,
          subRefCount: updatedRefs,
          subTeardownTimers: updatedTimers,
        };
      });
    });
  },

  // 5. Global Reset
  clearAllSubscriptions: () => {
    const { subscriptions, subTeardownTimers } = get();

    Object.values(subTeardownTimers).forEach((timerId) =>
      clearTimeout(timerId),
    );
    Object.values(subscriptions).forEach((unsubscribe) => {
      if (typeof unsubscribe === "function") unsubscribe();
    });

    set({
      subscriptions: {},
      queryCache: {},
      subRefCount: {},
      subTeardownTimers: {},
      loading: false,
    });
  },
}));
